import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove, sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { coverUri } from "../lib/api";
import { usePlayer, songsQueue } from "../lib/player";
import { Button, EmptyState } from "./ui";
import { Icon } from "./icons";

function Row({ s, n, sortable, playing, onPlay, onOpen, actions, testid }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: s.id, disabled: !sortable });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style} className={`track-row ${playing ? "playing" : ""} ${isDragging ? "dragging" : ""}`} data-testid={testid} data-playing={playing ? "true" : "false"}>
      {sortable ? (
        <button className="drag-handle" aria-label={`Arrastar ${s.title}`} {...attributes} {...listeners} data-testid={`${testid}-drag`}><Icon name="grip" size={18} /></button>
      ) : null}
      <button className="icon-btn" onClick={onPlay} aria-label={`Reproduzir ${s.title}`} data-testid={`${testid}-play`} style={{ width: 34, height: 34, flexShrink: 0 }}>
        {playing ? <Icon name="volume-high" size={16} color="var(--brand-primary)" /> : <span className="track-num">{n}</span>}
      </button>
      <img className="cover" src={coverUri(s.cover_url)} alt="" style={{ width: 44, height: 44, borderRadius: 6, flexShrink: 0 }} />
      <button onClick={onOpen} data-testid={`${testid}-open`} style={{ flex: 1, minWidth: 0, background: "none", border: "none", color: "inherit", textAlign: "left", cursor: "pointer", padding: 0 }}>
        <div style={{ fontFamily: "var(--f-medium)", fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: playing ? "var(--brand-primary)" : undefined }}>{s.title}</div>
        <div className="t-hint" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.artist}</div>
      </button>
      {actions}
    </div>
  );
}

/** Spotify-like numbered track list: play all, play from any song, current track marker, optional drag & drop. */
export function TrackList({ songs, ctx, canReorder = false, onReorder, buildQueue, rowActions, testid = "track", emptyText = "Ainda sem músicas." }) {
  const player = usePlayer();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 120, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const active = player.context?.key === ctx.key;
  const play = async (i) => {
    setBusy(true);
    try { player.playQueue(buildQueue ? await buildQueue() : songsQueue(songs), i, ctx); }
    finally { setBusy(false); }
  };
  const onDragEnd = ({ active: a, over }) => {
    if (!over || a.id === over.id) return;
    const ids = songs.map((s) => s.id);
    onReorder(arrayMove(ids, ids.indexOf(a.id), ids.indexOf(over.id)));
  };
  if (!songs.length) return <EmptyState icon="list-music" text={emptyText} />;
  return (
    <div data-testid={`${testid}-list`}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
        <Button className="auto" icon={active && player.status.playing ? "pause" : "play"} loading={busy}
          onClick={() => (active ? player.toggle() : play(0))} data-testid={`${testid}-play-all`}>
          {active && player.status.playing ? "PAUSA" : active ? "CONTINUAR" : "REPRODUZIR TUDO"}
        </Button>
        <span className="t-hint">{songs.length} {songs.length === 1 ? "música" : "músicas"}{canReorder ? " · arraste para reordenar" : " · ordem oficial"}</span>
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={songs.map((s) => s.id)} strategy={verticalListSortingStrategy}>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {songs.map((s, i) => (
              <Row key={s.id} s={s} n={i + 1} sortable={canReorder} testid={`${testid}-${i}`}
                playing={active && player.item?.song_id === s.id}
                onPlay={() => (active && player.item?.song_id === s.id ? player.toggle() : play(i))}
                onOpen={() => navigate(`/song/${s.id}`, { state: { from: ctx } })}
                actions={rowActions?.(s, i)} />
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  );
}
